<template>
  <v-main>
    <v-container fluid>
      <!-- Loading State -->
      <v-row v-if="loading">
        <v-col cols="12" class="text-center py-12">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
          <p class="mt-4 text-h6">Loading product details...</p>
        </v-col>
      </v-row>

      <!-- Product Details -->
      <template v-else-if="product">
        <v-row>
          <v-col md="12">
            <v-card elevation="6" rounded="xxl">
              <v-card-title>
                <v-btn
                  class="mx-2"
                  fab
                  x-small
                  color="primary"
                  @click="$router.back()"
                >
                  <v-icon dark>mdi-arrow-left</v-icon>
                </v-btn>
                <h3 class="font-weight-bold">Scanned Product Details</h3>
                <v-spacer></v-spacer>
                <v-chip
                  :color="product.in_stock ? 'success' : 'error'"
                  dark
                  class="mr-2"
                >
                  {{ product.stock_status }}
                </v-chip>
              </v-card-title>
            </v-card>
          </v-col>
        </v-row>

        <!-- Product Information Card -->
        <v-row class="mt-4">
          <v-col md="6">
            <v-card elevation="6" rounded="xxl">
              <v-card-text>
                <!-- Product Images -->
                <v-carousel
                  v-if="product.product_variant_images && product.product_variant_images.length > 0"
                  height="400"
                  cycle
                  show-arrows-on-hover
                >
                  <v-carousel-item
                    v-for="(image, i) in product.product_variant_images"
                    :key="i"
                    :src="getImageUrl(image.image_path)"
                  ></v-carousel-item>
                </v-carousel>
                <div v-else class="text-center pa-8">
                  <v-icon size="120" color="grey lighten-2">mdi-image-off</v-icon>
                  <p class="text-subtitle-1 grey--text mt-2">No images available</p>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col md="6">
            <v-card elevation="6" rounded="xxl">
              <v-card-text>
                <h2 class="text-h4 font-weight-bold mb-2">
                  {{ product.product ? product.product.name : "Product" }}
                </h2>

                <div class="mb-4">
                  <v-chip small class="mr-2">
                    <v-icon left small>mdi-palette</v-icon>
                    {{ product.color }}
                  </v-chip>
                  <v-chip small>
                    <v-icon left small>mdi-ruler</v-icon>
                    {{ product.size }}
                  </v-chip>
                </div>

                <!-- Price Information -->
                <div class="mb-4">
                  <div class="d-flex align-center">
                    <span class="text-h4 primary--text font-weight-bold mr-2">
                      ₹{{ product.price }}
                    </span>
                    <span v-if="product.mrp > product.price" class="text-h6 grey--text text-decoration-line-through">
                      ₹{{ product.mrp }}
                    </span>
                  </div>
                  <p v-if="product.savings_amount > 0" class="success--text text-subtitle-1">
                    You save: ₹{{ product.savings_amount }}
                    ({{ ((product.savings_amount / product.mrp) * 100).toFixed(0) }}% OFF)
                  </p>
                </div>

                <!-- Stock Information for Distributor/SE -->
                <v-alert
                  v-if="product.assigned_stock"
                  type="info"
                  outlined
                  dense
                  class="mb-4"
                >
                  <div class="font-weight-bold mb-2">Your Stock Details:</div>
                  <v-row dense>
                    <v-col cols="4">
                      <div class="text-caption">Available</div>
                      <div class="text-h6">{{ product.assigned_stock.available_quantity }}</div>
                    </v-col>
                    <v-col cols="4">
                      <div class="text-caption">Assigned</div>
                      <div class="text-h6">{{ product.assigned_stock.assigned_quantity }}</div>
                    </v-col>
                    <v-col cols="4">
                      <div class="text-caption">Sold</div>
                      <div class="text-h6">{{ product.assigned_stock.sold_quantity }}</div>
                    </v-col>
                  </v-row>
                </v-alert>

                <!-- Product Description -->
                <div v-if="product.product && product.product.description" class="mb-4">
                  <h3 class="text-h6 mb-2">Description</h3>
                  <p class="text-body-1">{{ product.product.description }}</p>
                </div>

                <!-- Category Information -->
                <div v-if="product.product && product.product.category" class="mb-4">
                  <h3 class="text-h6 mb-2">Category</h3>
                  <v-chip color="primary" outlined>
                    {{ product.product.category.name }}
                  </v-chip>
                </div>

                <!-- Action Buttons -->
                <v-divider class="my-4"></v-divider>
                <div class="d-flex flex-wrap gap-2">
                  <v-btn
                    color="primary"
                    large
                    block
                    @click="createOrder"
                    :disabled="!product.in_stock"
                    :loading="creatingOrder"
                  >
                    <v-icon left>mdi-cart-plus</v-icon>
                    Create Order
                  </v-btn>

                  <v-btn
                    color="secondary"
                    large
                    block
                    outlined
                    @click="scanAnother"
                  >
                    <v-icon left>mdi-qrcode-scan</v-icon>
                    Scan Another Product
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Other Variants -->
        <v-row v-if="product.product && product.product.variants && product.product.variants.length > 1" class="mt-4">
          <v-col cols="12">
            <v-card elevation="6" rounded="xxl">
              <v-card-title>
                <h3 class="font-weight-bold">Other Available Variants</h3>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col
                    v-for="variant in otherVariants"
                    :key="variant.id"
                    cols="12"
                    sm="6"
                    md="4"
                  >
                    <v-card outlined>
                      <v-img
                        v-if="variant.product_variant_images && variant.product_variant_images.length > 0"
                        :src="getImageUrl(variant.product_variant_images[0].image_path)"
                        height="200"
                      ></v-img>
                      <v-card-text>
                        <div class="mb-2">
                          <v-chip x-small class="mr-1">{{ variant.color }}</v-chip>
                          <v-chip x-small>{{ variant.size }}</v-chip>
                        </div>
                        <div class="text-h6 primary--text">₹{{ variant.price }}</div>
                        <div v-if="variant.mrp > variant.price" class="text-caption grey--text text-decoration-line-through">
                          ₹{{ variant.mrp }}
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- Error State -->
      <v-row v-else>
        <v-col cols="12">
          <v-alert type="error" prominent>
            <v-row align="center">
              <v-col class="grow">
                Product not found or invalid QR code.
              </v-col>
              <v-col class="shrink">
                <v-btn color="white" outlined @click="scanAnother">
                  Scan Again
                </v-btn>
              </v-col>
            </v-row>
          </v-alert>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "ScannedProductDetail",
  data() {
    return {
      product: null,
      loading: true,
      creatingOrder: false,
    };
  },
  computed: {
    otherVariants() {
      if (!this.product || !this.product.product || !this.product.product.variants) {
        return [];
      }
      return this.product.product.variants.filter(v => v.id !== this.product.id);
    },
  },
  mounted() {
    this.fetchProductDetails();
  },
  methods: {
    async fetchProductDetails() {
      this.loading = true;

      try {
        const qrCode = this.$route.params.qrCode;
        const response = await axios.post("scan-product-qr", {
          qr_code_data: qrCode,
        });

        if (response.data.success) {
          this.product = response.data.data;
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        this.$toast.error(
          error.response?.data?.message || "Failed to load product details"
        );
      } finally {
        this.loading = false;
      }
    },

    getImageUrl(imagePath) {
      if (!imagePath) return "";
      // Assuming media URL is stored in Vuex or environment
      const baseUrl = this.$store.state.settings.mediaUrl || "";
      return baseUrl + imagePath;
    },

    async createOrder() {
      // Navigate to order creation page with pre-filled product variant
      this.$router.push({
        name: "CreateOrder",
        query: {
          variant_id: this.product.id,
          product_id: this.product.product_id,
        },
      });
    },

    scanAnother() {
      this.$router.push({ name: "QRScanner" });
    },
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
